/*** RANKING ***/

// Libraries
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

enum userType { default, hidden }
enum isEmpty { no, yes }

export default class Ranking implements RankingInterface {
    userData: ScoreRankingInterface;
    rankingList: Array<ScoreRankingInterface>;
    wrapperUserId: string;
    wrapperRankingListId: string;
    mainRankingClass: string;
    cardRankingClass: string;
    totalRegistersShown: number;

    constructor({ wrapperUserId, wrapperRankingListId, userData, rankingList, prefix = 'custom', totalRegistersShown = 5 }: RankingInterface) {
        this.userData = userData;
        this.rankingList = rankingList;
        this.wrapperUserId = wrapperUserId;
        this.wrapperRankingListId = wrapperRankingListId;
        this.mainRankingClass = `${prefix}-table-ranking`;
        this.cardRankingClass = `${prefix}-ranking-card`;
        this.totalRegistersShown = totalRegistersShown;
        this.setContentUser();
        this.setContentList();
    }

    // AGREGA AL DOM LOS DATOS DEL USUARIO
    setContentUser(): void {
        let refWrapperUser: HTMLElement = document.querySelector(this.wrapperUserId);
        if (refWrapperUser) refWrapperUser.innerHTML = ""
        if (this.userData && refWrapperUser) {
            let infoUser: string = this.markupCardUser(this.userData);
            refWrapperUser.insertAdjacentHTML('afterbegin', infoUser);
        }
    };

    // AGREGA AL DOM LOS DATOS DE LA LISTA DEL RANKING
    setContentList(): void {
        let refWrapperRanking: HTMLElement = document.querySelector(this.wrapperRankingListId);
        if (refWrapperRanking) refWrapperRanking.innerHTML = ""

        if (refWrapperRanking && this.rankingList.length > 0) {
            this.toggleEmptyClass(refWrapperRanking, isEmpty.no)
            refWrapperRanking.insertAdjacentHTML('beforeend', this.setFieldsName());
            for (let i = 0; i < this.rankingList.length; i++) {
                let infoRanking: string = i >= this.totalRegistersShown ?
                    this.markupCardUser(this.rankingList[i], userType.hidden) : this.markupCardUser(this.rankingList[i]);
                refWrapperRanking.insertAdjacentHTML('beforeend', infoRanking);
            }

            this.hideLoader()
            this.animateCardList()
        }
        else if (refWrapperRanking) {
            this.toggleEmptyClass(refWrapperRanking, isEmpty.yes)
            refWrapperRanking.insertAdjacentHTML('beforeend', `<span class="${this.mainRankingClass}__empty-message">No hay datos aún</span>`);
        }

        this.setLoadMoreButton(refWrapperRanking)
    };

    // CREA LOS NOMBRES DE LOS CAMPOS
    setFieldsName(): string {
        return `<div class="${this.mainRankingClass}__field">
                    <span class="${this.mainRankingClass}__field-name">Nombre</span>
                    <span class="${this.mainRankingClass}__field-points">Puntos</span>
                </div>`
    }

    // CREA EL BOTON DE "CARGAR MAS" EN CASO DE QUE HAYAN MAS REGISTROS 
    // DE LOS QUE SE VAN A MOSTRAR INICIALMENTE (totalRegistersShown)
    setLoadMoreButton(refWrapperRanking: HTMLElement): void {
        if (this.rankingList.length > this.totalRegistersShown) {
            new Button(this.wrapperRankingListId, {
                text: 'Cargar más',
                classes: [`${this.mainRankingClass}__button`],
                onClick: (e) => {
                    this.showMore(e)
                }
            })
        }
    }

    // MUESTRA LOS REGISTROS OCULTOS (VER MAS)
    showMore(e: any) {
        let { list, thereAreMore } = this.getHiddenRegister()
        list.forEach(elm => {
            elm.classList.remove(`${this.cardRankingClass}--hidden`)
            elm.classList.add(`${this.cardRankingClass}--enabled`)
        })
        gsap.to(list, { duration: 1, opacity: 1, translateY: 0, ease: 'power1.inOut', stagger: .1 })
        if (!thereAreMore) e.target.remove()
    }

    // OBTIENE LOS REGISTROS OCULTOS (VER MAS)
    getHiddenRegister(): { list: Array<HTMLElement>, thereAreMore: boolean } {
        let refHiddenRegister: any = document.querySelectorAll(`${this.wrapperRankingListId} .${this.cardRankingClass}--hidden`)
        let thereAreMoreRegister: boolean = refHiddenRegister.length > this.totalRegistersShown
        return { list: [...refHiddenRegister].slice(0, this.totalRegistersShown), thereAreMore: thereAreMoreRegister }
    }

    // ESTABLECE EL RANKING DEL USUARIO LOGEADO
    setUserData(userData: ScoreRankingInterface): void {
        this.userData = userData;
        this.setContentUser();
    }

    // ESTABLECE LA LISTA DE RANKING
    setRankingList(rankingList: Array<ScoreRankingInterface>): void {
        this.rankingList = rankingList;
        this.setContentList();
    }

    // MUESTRA MENSAJE EN CASO DE QUE NO HAYAN REGISTROS PARA MOSTRAR
    toggleEmptyClass(refWrapperRanking: HTMLElement, isEmpty: isEmpty): void {
        switch (isEmpty) {
            case 0:
                refWrapperRanking.classList.remove(`${this.mainRankingClass}--empty`)
                break;

            case 1:
                refWrapperRanking.classList.add(`${this.mainRankingClass}--empty`)
                break;
        }
    }

    // FUNCION PARA PERSONALIZAR EL HTML DE LAS CARDS DEL RANKING
    markupCardUser({ pos, firstname, lastname, points }: ScoreRankingInterface, currentUser: userType = userType.default): string {
        const cardState = [`${this.cardRankingClass}--enabled`, `${this.cardRankingClass}--hidden`]
        const currentClassUser = this.userData && this.userData.pos == pos ? `${this.cardRankingClass}--current` : ''
        const cardClasses = [this.cardRankingClass, cardState[currentUser], currentClassUser]

        return `<div class="${cardClasses.join(' ').trim()}">
                    <span class="${this.cardRankingClass}__position">${pos}</span>
                    <span class="${this.cardRankingClass}__name">${firstname.split(' ')[0]} ${lastname.split(' ')[0]}</span>
                    <span class="${this.cardRankingClass}__points">${points} Pts</span>
                </div>`
    }

    // HIDE LOADER
    hideLoader(): void {
        let refLoader: HTMLElement = document.querySelector('.ranking-loader')
        if (refLoader) {
            refLoader.classList.add('hide')
            setTimeout(() => refLoader.remove(), 1000)
        }
    }

    // ANIMATE CARDS
    animateCardList(): void {
        // Animate card / Animate field
        gsap.to(`.${this.mainRankingClass}__field`, { duration: 1, opacity: 1, translateY: 0, ease: 'power1.inOut' })
        gsap.to(`#infoUser .${this.cardRankingClass}--enabled`, { duration: 1, opacity: 1, translateY: 0, ease: 'power1.inOut' })
        gsap.to(`#rankingList .${this.cardRankingClass}--enabled`, { duration: 1, opacity: 1, translateY: 0, ease: 'power1.inOut', stagger: .1 })
            .then(() => {
                gsap.to(`#rankingList .${this.mainRankingClass}__button`, { duration: .5, opacity: 1, ease: 'power1.inOut' })
            })
    }
}

class Button implements ButtonInterface {
    buttonElement: HTMLButtonElement;
    parentSelectorId: string;
    options: ButtonOptions;

    constructor(parentSelectorId: string, { text, classes, onClick }: ButtonOptions) {
        this.parentSelectorId = parentSelectorId;
        this.options = { text, classes, onClick };
        this.onClick = onClick;

        this.createElement();
    }

    createElement(): void {
        let refElement: HTMLButtonElement = document.createElement('button')
        let { text, classes } = this.options
        refElement.innerHTML = text
        refElement.classList.add(...classes)
        refElement.addEventListener('click', (e) => this.onClick(e))

        this.buttonElement = refElement
        this.appendElement()
    }

    appendElement(): void {
        let refParent: HTMLElement = document.querySelector(this.parentSelectorId)
        refParent.insertAdjacentElement('beforeend', this.buttonElement)
    }

    onClick(event: Event): void { }
}

// INTERFACES
export interface UserInterface {
    uid: string;
    name: string;
    lastname: string;
}

export interface RankingInterface {
    userData: ScoreRankingInterface;
    rankingList: Array<ScoreRankingInterface>;
    wrapperUserId: string;
    wrapperRankingListId: string;
    prefix?: string;
    totalRegistersShown?: number;
}

export interface ScoreRankingInterface {
    pos: number;
    firstname: string;
    lastname: string;
    points: string;
}

export interface ButtonInterface {
    parentSelectorId: string;
    options?: ButtonOptions;
}

export interface ButtonOptions {
    text: string;
    classes?: Array<string>;
    onClick?(e: any): void;
}